/**
 * MaintenanceTasks — aggregated view of all items that are due for review.
 * Pulls from: risks, controls, evidence, incidents, issues, threats,
 * remediations, documents, PESTLE assessments, and interested parties.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  riskApi,
  listApi,
  pestleApi,
  interestedPartiesApi,
  evidenceSubmissionsApi,
  peopleApi,
  registersApi,
  incidentRegistersApi,
  issueRegistersApi,
  remediationRegistersApi,
  threatRegistersApi,
  evidenceRegistersApi,
  documentRegistersApi,
  controlMatrixApi,
} from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';

// ─── Types ──────────────────────────────────────────────────────────────────

type ItemType =
  | 'Risk'
  | 'Control'
  | 'Evidence'
  | 'Evidence Collection'
  | 'Incident'
  | 'Issue'
  | 'Threat'
  | 'Remediation'
  | 'Document'
  | 'PESTLE'
  | 'Interested Party'
  | 'Evidence Submission';

type TaskItem = {
  id: string;
  type: ItemType;
  title: string;
  dueDate: string | null; // ISO date string or null
  owner: string;
  statusLabel: string;
  link: string;
  register: string;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

const parseDateStr = (val: any): string | null => {
  if (!val) return null;
  const s = String(val).trim();
  if (!s) return null;
  // Accept YYYY-MM-DD or ISO
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

const FREQ_TO_MONTHS: Record<string, number> = {
  Weekly: 0.25,
  'Bi-weekly': 0.5,
  Monthly: 1,
  Quarterly: 3,
  'Bi-annually': 6,
  Annually: 12,
};

/** Compute a future date from a base date + frequency string. Returns YYYY-MM-DD or null. */
const calcNextDate = (fromDate: string, frequency: string): string | null => {
  const months = FREQ_TO_MONTHS[frequency];
  if (!fromDate || months === undefined) return null;
  const d = new Date(fromDate);
  if (isNaN(d.getTime())) return null;
  if (months < 1) {
    d.setDate(d.getDate() + Math.round(months * 28));
  } else {
    d.setMonth(d.getMonth() + months);
  }
  return d.toISOString().slice(0, 10);
};

const getField = (fields: any, ...keys: string[]): string => {
  if (!fields || typeof fields !== 'object') return '';
  for (const k of keys) {
    if (fields[k] !== undefined && fields[k] !== null && fields[k] !== '') {
      return String(fields[k]);
    }
  }
  return '';
};

// Values are np-tone-* suffixes (see nimbus-premium.css) — secondary/dark both
// map to the neutral tone since the design system doesn't define those tones.
const TYPE_COLOURS: Record<ItemType, string> = {
  Risk: 'danger',
  Control: 'primary',
  Evidence: 'info',
  'Evidence Collection': 'info',
  Incident: 'warning',
  Issue: 'neutral',
  Threat: 'neutral',
  Remediation: 'success',
  Document: 'neutral',
  PESTLE: 'info',
  'Interested Party': 'primary',
  'Evidence Submission': 'warning',
};

// ─── Normalisation helpers ───────────────────────────────────────────────────

const normaliseRisks = (items: any[]): TaskItem[] =>
  items.map((r) => ({
    id: r.riskId || r.id,
    type: 'Risk',
    title: getField(r.fields, 'title', 'risk_title') || r.riskId || 'Untitled Risk',
    dueDate: parseDateStr(getField(r.fields, 'review_date', 'next_review_date', 'control_next_review')),
    owner: getField(r.fields, 'owner', 'risk_owner', 'assignedTo', 'assigned_to'),
    statusLabel: r.status || '',
    link: `/risks/${r.riskId || r.id}`,
    register: r.registerId || r.registerName || '',
  }));

const normaliseControls = (items: any[]): TaskItem[] =>
  items.map((c) => ({
    id: c.controlId || c.id,
    type: 'Control',
    title: getField(c.fields, 'title', 'control_title', 'control_name') || c.controlId || 'Untitled Control',
    dueDate: parseDateStr(getField(c.fields, 'control_next_review', 'next_review_date', 'review_date')),
    owner: getField(c.fields, 'owner', 'control_owner', 'assignedTo'),
    statusLabel: c.status || getField(c.fields, 'status', 'control_status') || '',
    // matrixId was already resolved below for `register` but never carried into the link
    // itself - without it, ControlDetail.tsx has no way to find the control's real field
    // config and falls back to its bare 3-field default, which can look like most of the
    // Control Details tab has gone missing.
    link: c.matrixId ? `/controls/${c.controlId || c.id}?matrixId=${encodeURIComponent(c.matrixId)}` : `/controls/${c.controlId || c.id}`,
    register: c.matrixId || c.registerId || c.matrixName || '',
  }));

const normaliseEvidence = (items: any[]): TaskItem[] =>
  items.map((e) => {
    // Prefer stored calculated value; fall back to computing from frequency + last reviewed
    const storedReview = getField(e.fields, 'evidence_next_review', 'next_review_date', 'review_date');
    const computedReview = storedReview
      ? storedReview
      : calcNextDate(
          getField(e.fields, 'evidence_last_reviewed'),
          getField(e.fields, 'evidence_review_frequency'),
        );
    return {
      id: e.evidenceId || e.id,
      type: 'Evidence',
      title: getField(e.fields, 'title', 'evidence_title', 'evidence_name') || e.evidenceId || 'Untitled Evidence',
      dueDate: parseDateStr(computedReview),
      owner: getField(e.fields, 'owner', 'evidence_owner', 'assignedTo'),
      statusLabel: e.status || getField(e.fields, 'status', 'evidence_status') || '',
      // RegisterItemDetail (what /evidence/:id actually renders) hard-requires a registerId
      // query param and shows a "Missing registerId" banner instead of the record without one -
      // confirmed live this was the entire cause of "error when clicking an evidence item in
      // the maintenance task list", not a routing or id-mismatch problem.
      link: e.registerId ? `/evidence/${e.evidenceId || e.id}?registerId=${encodeURIComponent(e.registerId)}` : `/evidence/${e.evidenceId || e.id}`,
      register: e.registerId || '',
    };
  });

/** Separate entry for collection schedule — uses next_due_date, falls back to computing from collection_frequency + evidence_last_reviewed */
const normaliseEvidenceCollection = (items: any[]): TaskItem[] =>
  items
    .map((e) => {
      const storedDue = getField(e.fields, 'next_due_date');
      const computedDue = storedDue
        ? storedDue
        : calcNextDate(
            getField(e.fields, 'evidence_last_reviewed'),
            getField(e.fields, 'collection_frequency'),
          );
      return computedDue
        ? {
            id: `col-${e.evidenceId || e.id}`,
            type: 'Evidence Collection' as ItemType,
            title: getField(e.fields, 'title', 'evidence_title', 'evidence_name') || e.evidenceId || 'Untitled Evidence',
            dueDate: parseDateStr(computedDue),
            owner: getField(e.fields, 'assigned_collector', 'owner', 'evidence_owner', 'assignedTo'),
            statusLabel: getField(e.fields, 'schedule_status') || '',
            link: e.registerId
              ? `/evidence/${e.evidenceId || e.id}?tab=schedule&registerId=${encodeURIComponent(e.registerId)}`
              : `/evidence/${e.evidenceId || e.id}?tab=schedule`,
            register: e.registerId || '',
          }
        : null;
    })
    .filter((x): x is TaskItem => x !== null);

const normaliseGeneric = (
  items: any[],
  type: ItemType,
  idField: string,
  titleFields: string[],
  reviewFields: string[],
  ownerFields: string[],
  basePath: string,
): TaskItem[] =>
  items.map((item) => {
    const rawId = item[idField] || item.id;
    return {
      id: rawId,
      type,
      title: getField(item.fields, ...titleFields) || rawId || `Untitled ${type}`,
      dueDate: parseDateStr(getField(item.fields, ...reviewFields)),
      owner: getField(item.fields, ...ownerFields),
      statusLabel: item.status || getField(item.fields, 'status') || '',
      // Same registerId requirement as normaliseEvidence above - Incident/Issue/Threat/
      // Remediation detail pages are all thin RegisterItemDetail wrappers too.
      link: item.registerId ? `/${basePath}/${rawId}?registerId=${encodeURIComponent(item.registerId)}` : `/${basePath}/${rawId}`,
      register: item.registerId || '',
    };
  });

const normaliseDocuments = (items: any[]): TaskItem[] =>
  items.map((d) => ({
    id: d.documentId || d.id,
    type: 'Document',
    title: d.title || 'Untitled Document',
    dueDate: parseDateStr(d.nextReviewDate || d.next_review_date),
    owner: d.author || d.owner || '',
    statusLabel: d.status || '',
    link: `/documents/${d.documentId || d.id}`,
    register: d.registerId || d.documentType || '',
  }));

// Produces one entry from the tenant-level PESTLE review settings
const normalisePestleSettings = (settings: { nextReviewDate?: string } | null): TaskItem[] => {
  if (!settings?.nextReviewDate) return [];
  return [{
    id: 'pestle-review',
    type: 'PESTLE',
    title: 'PESTLE Review',
    dueDate: parseDateStr(settings.nextReviewDate),
    owner: '',
    statusLabel: '',
    link: '/pestle',
    register: '',
  }];
};

const normaliseInterestedParties = (items: any[]): TaskItem[] =>
  items.map((p) => ({
    id: p.partyId,
    type: 'Interested Party',
    title: p.name || 'Unnamed Party',
    dueDate: parseDateStr(p.nextReviewDate || p.next_review_date),
    owner: p.owner || p.contact || '',
    statusLabel: p.complianceStatus || '',
    link: `/interested-parties`,
    register: p.category || '',
  }));

/**
 * Normalise pending evidence submissions.
 * dueDate = submittedAt + 7 days (review window from point of submission).
 */
const normaliseEvidenceSubmissions = (items: any[]): TaskItem[] =>
  items.map((s) => {
    const submitted = new Date(s.submittedAt);
    submitted.setDate(submitted.getDate() + 7);
    const dueDate = submitted.toISOString().slice(0, 10);
    const titlePrefix = s.evidenceTitle || s.evidenceId || 'Evidence';
    const noteSnippet = s.notes ? ` — ${String(s.notes).slice(0, 60)}` : '';
    return {
      id: s.submissionId,
      type: 'Evidence Submission' as ItemType,
      title: `${titlePrefix}${noteSnippet}`,
      dueDate,
      owner: s.submittedBy || '',
      statusLabel: s.status || '',
      link: s.evidenceRegisterId
        ? `/evidence/${s.evidenceId}?tab=schedule&registerId=${encodeURIComponent(s.evidenceRegisterId)}`
        : `/evidence/${s.evidenceId}?tab=schedule`,
      register: s.evidenceRegisterId || '',
    };
  });

// ─── Week boundary helpers ───────────────────────────────────────────────────

/** Monday of the current week at 00:00 */
const getStartOfWeek = (d: Date): Date => {
  const r = new Date(d);
  const day = r.getDay(); // 0=Sun
  r.setDate(r.getDate() - (day === 0 ? 6 : day - 1));
  r.setHours(0, 0, 0, 0);
  return r;
};

/** Sunday of the current week at 23:59:59 */
const getEndOfWeek = (d: Date): Date => {
  const r = getStartOfWeek(d);
  r.setDate(r.getDate() + 6);
  r.setHours(23, 59, 59, 999);
  return r;
};

const START_OF_WEEK = getStartOfWeek(TODAY);
const END_OF_WEEK   = getEndOfWeek(TODAY);

type Section = 'past-due' | 'this-week' | 'future';

const getSection = (dueDate: string): Section => {
  const d = new Date(dueDate);
  d.setHours(0, 0, 0, 0);
  if (d < START_OF_WEEK) return 'past-due';
  if (d <= END_OF_WEEK)  return 'this-week';
  return 'future';
};

const SECTION_META: Record<Section, { label: string; colour: string; icon: string }> = {
  'past-due':  { label: 'Past Due',       colour: 'danger',  icon: 'ri-alarm-warning-line' },
  'this-week': { label: 'To Do This Week', colour: 'warning', icon: 'ri-calendar-event-line' },
  'future':    { label: 'Future Tasks',    colour: 'success', icon: 'ri-calendar-line' },
};

const ALL_TYPES: ItemType[] = [
  'Risk', 'Control', 'Evidence', 'Evidence Collection', 'Incident', 'Issue',
  'Threat', 'Remediation', 'Document', 'PESTLE', 'Interested Party', 'Evidence Submission',
];

// ─── No-date callout ──────────────────────────────────────────────────────────

const NoDateCallout: React.FC<{ items: TaskItem[] }> = ({ items }) => {
  const [collapsed, setCollapsed] = React.useState(true);
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(items, 25);
  return (
    <div className="np-card">
      <div
        className="np-card-head"
        style={{ padding: '1rem 1.25rem', cursor: 'pointer', userSelect: 'none', borderBottom: collapsed ? 'none' : '1px solid var(--np-border)' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <i className="ri-calendar-2-line" style={{ color: 'var(--bs-secondary-color)' }}></i>
        <h2 className="np-mb-0">No Review Date Set</h2>
        <span className="np-chip np-tone-neutral np-tabular">{items.length}</span>
        <span className="np-sub np-mb-0">— these items won't appear in scheduled sections until a review date is added</span>
        <button type="button" className="np-icon-btn" title={collapsed ? 'Expand' : 'Collapse'}>
          <i className={`ri-arrow-${collapsed ? 'down' : 'up'}-s-line`}></i>
        </button>
      </div>
      {!collapsed && (
        <div className="np-card-body p-0">
          <NimbusTable
            resizable={false}
            colgroup={<>
              <col style={{ width: '130px' }} />
              <col />
              <col style={{ width: '200px' }} />
              <col style={{ width: '160px' }} />
              <col style={{ width: '60px' }} />
            </>}
            thead={<tr>
              <th>Type</th>
              <th>Title</th>
              <th>Register / Category</th>
              <th>Owner</th>
              <th></th>
            </tr>}>
            {pageItems.map((item) => (
              <tr key={`nd-${item.type}-${item.id}`}>
                <td>
                  <span className={`np-chip np-tone-${TYPE_COLOURS[item.type]}`}>
                    {item.type}
                  </span>
                </td>
                <td>
                  <Link to={item.link} className="np-row-title">
                    {item.title}
                  </Link>
                </td>
                <td><span className="np-sub np-mb-0">{item.register || '—'}</span></td>
                <td>{item.owner || <span className="np-sub np-mb-0">—</span>}</td>
                <td className="text-center">
                  <Link to={item.link} className="np-icon-btn" title="Open item">
                    <i className="ri-external-link-line"></i>
                  </Link>
                </td>
              </tr>
            ))}
          </NimbusTable>
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      )}
    </div>
  );
};

// ─── Section table sub-component ─────────────────────────────────────────────

const SectionTable: React.FC<{
  section: Section;
  items: TaskItem[];
  formatDate: (d: string | null) => React.ReactNode;
}> = ({ section, items, formatDate }) => {
  const { label, colour, icon } = SECTION_META[section];
  const [collapsed, setCollapsed] = React.useState(false);
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(items, 25);

  return (
    <div className={`np-card np-card-accent-${colour}`}>
      <div
        className="np-card-head"
        style={{ padding: '1rem 1.25rem', cursor: 'pointer', userSelect: 'none', borderBottom: collapsed ? 'none' : '1px solid var(--np-border)' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <i className={`${icon} text-${colour}`}></i>
        <h2 className="np-mb-0">{label}</h2>
        <span className={`np-chip np-tone-${colour} np-tabular`}>{items.length}</span>
        <button type="button" className="np-icon-btn" title={collapsed ? 'Expand' : 'Collapse'}>
          <i className={`ri-arrow-${collapsed ? 'down' : 'up'}-s-line`}></i>
        </button>
      </div>

      {!collapsed && (
        <>
        <NimbusTable
          style={{ tableLayout: 'fixed', minWidth: '800px' }}
          colgroup={<>
            <col style={{ width: '130px' }} />
            <col />
            <col style={{ width: '200px' }} />
            <col style={{ width: '160px' }} />
            <col style={{ width: '155px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '52px' }} />
          </>}
          thead={<tr>
            <th>Type</th>
            <th>Title</th>
            <th>Register / Category</th>
            <th>Owner</th>
            <th>Review Date</th>
            <th>Status</th>
            <th></th>
          </tr>}>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4 np-sub">
                    <i className="ri-checkbox-circle-line me-2 text-success"></i>
                    No items in this section
                  </td>
                </tr>
              ) : (
                pageItems.map((item) => (
                  <tr key={`${item.type}-${item.id}`}>
                    <td>
                      <span className={`np-chip np-tone-${TYPE_COLOURS[item.type]}`}>
                        {item.type}
                      </span>
                    </td>
                    <td>
                      <Link to={item.link} className="np-row-title">
                        {item.title}
                      </Link>
                    </td>
                    <td>
                      <span className="np-sub np-mb-0">{item.register || '—'}</span>
                    </td>
                    <td>
                      {item.owner || <span className="np-sub np-mb-0">—</span>}
                    </td>
                    <td>
                      {item.dueDate ? (
                        <span className={`np-chip np-tone-${colour} np-tabular`}>
                          {section === 'past-due' && <i className="ri-alarm-warning-line"></i>}
                          {formatDate(item.dueDate)}
                        </span>
                      ) : (
                        <span className="np-sub np-mb-0">Not set</span>
                      )}
                    </td>
                    <td>
                      {item.statusLabel ? (
                        <span className="np-chip np-tone-neutral" style={{ textTransform: 'capitalize' }}>
                          {item.statusLabel}
                        </span>
                      ) : (
                        <span className="np-sub np-mb-0">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Link to={item.link} className="np-icon-btn" title="Open item">
                        <i className="ri-external-link-line"></i>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
        </NimbusTable>
        {pageItems.length > 0 && (
          <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
        )}
        </>
      )}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const resolvePersonId = (value: string, personMap: Record<string, string>): string => {
  if (!value) return value;
  if (personMap[value]) return personMap[value];
  return /^PERSON-/i.test(value) ? 'Unknown User' : value;
};

export const MaintenanceTasks: React.FC = () => {
  const [filterType, setFilterType] = useState<ItemType | 'All'>('All');
  const [search, setSearch] = useState('');

  const { data: people = [] } = useQuery({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const personMap = useMemo(() => {
    const map: Record<string, string> = {};
    people.forEach((p: any) => {
      if (p.personId) map[p.personId] = p.personName || p.personId;
    });
    return map;
  }, [people]);

  // Parallel queries — staleTime: 0 ensures fresh data on every visit
  const results = useQueries({
    queries: [
      { queryKey: ['risks-all-maint'],            queryFn: () => riskApi.listAll(),                                                                                    staleTime: 0 },
      { queryKey: ['controls-all-maint'],         queryFn: () => listApi.controls(),                                                                                   staleTime: 0 },
      { queryKey: ['evidence-all-maint'],         queryFn: () => listApi.evidence(),                                                                                   staleTime: 0 },
      { queryKey: ['incidents-all-maint'],        queryFn: () => listApi.incidents(),                                                                                  staleTime: 0 },
      { queryKey: ['issues-all-maint'],           queryFn: () => listApi.issues(),                                                                                     staleTime: 0 },
      { queryKey: ['threats-all-maint'],          queryFn: () => listApi.threats(),                                                                                    staleTime: 0 },
      { queryKey: ['remediations-all-maint'],     queryFn: () => listApi.remediations(),                                                                               staleTime: 0 },
      { queryKey: ['documents-all-maint'],        queryFn: () => listApi.documents(),                                                                                  staleTime: 0 },
      { queryKey: ['pestle-all-maint'],           queryFn: async () => { const r = await pestleApi.getSettings(); return (r as any).settings || null; },               staleTime: 0 },
      { queryKey: ['interested-parties-maint'],   queryFn: () => interestedPartiesApi.getAll(),                                                                        staleTime: 0 },
      { queryKey: ['evidence-submissions-maint'], queryFn: async () => { const r = await evidenceSubmissionsApi.listAllPending(); return (r as any).submissions || []; }, staleTime: 0 },
    ],
  });

  const isLoading = results.some((r) => r.isLoading);
  // Only fire register lookups after the main data batch completes, to avoid
  // exceeding the account-level Lambda concurrency limit.
  const mainDataLoaded = !isLoading && results.every((r) => !r.isPending);

  // Register queries for resolving IDs to names (staggered after main data)
  const registerResults = useQueries({
    queries: [
      { queryKey: ['registers-risk-maint'],        queryFn: () => registersApi.getAll(),            staleTime: 0, enabled: mainDataLoaded },
      { queryKey: ['registers-incident-maint'],    queryFn: () => incidentRegistersApi.getAll(),    staleTime: 0, enabled: mainDataLoaded },
      { queryKey: ['registers-issue-maint'],       queryFn: () => issueRegistersApi.getAll(),       staleTime: 0, enabled: mainDataLoaded },
      { queryKey: ['registers-remediation-maint'], queryFn: () => remediationRegistersApi.getAll(), staleTime: 0, enabled: mainDataLoaded },
      { queryKey: ['registers-threat-maint'],      queryFn: () => threatRegistersApi.getAll(),      staleTime: 0, enabled: mainDataLoaded },
      { queryKey: ['registers-evidence-maint'],    queryFn: () => evidenceRegistersApi.getAll(),    staleTime: 0, enabled: mainDataLoaded },
      { queryKey: ['registers-document-maint'],    queryFn: () => documentRegistersApi.getAll(),    staleTime: 0, enabled: mainDataLoaded },
      { queryKey: ['registers-matrix-maint'],      queryFn: () => controlMatrixApi.getAll(),        staleTime: 0, enabled: mainDataLoaded },
    ],
  });

  const registerMap = useMemo(() => {
    const map: Record<string, string> = {};
    registerResults.forEach((r) => {
      const items: any[] = r.data || [];
      items.forEach((reg: any) => {
        const id = reg.registerId || reg.matrixId;
        const name = reg.name || reg.registerName || reg.matrixName;
        if (id && name) map[id] = name;
      });
    });
    return map;
  }, [registerResults]);

  const allItems = useMemo((): TaskItem[] => {
    const [risks, controls, evidence, incidents, issues, threats, remediations, documents, pestle, parties, pendingSubmissions] = results;
    const raw = [
      ...normaliseRisks(risks.data || []),
      ...normaliseControls(controls.data || []),
      ...normaliseEvidence(evidence.data || []),
      ...normaliseEvidenceCollection(evidence.data || []),
      ...normaliseGeneric(incidents.data || [], 'Incident', 'incidentId', ['title', 'incident_title'], ['incident_next_review_date', 'next_review_date'], ['owner', 'assignedTo', 'assigned_to'], 'incidents'),
      ...normaliseGeneric(issues.data || [], 'Issue', 'issueId', ['title', 'issue_title'], ['issue_next_review_date', 'next_review_date'], ['owner', 'assignedTo', 'assigned_to'], 'issues'),
      ...normaliseGeneric(threats.data || [], 'Threat', 'threatId', ['threat_name', 'title', 'threat_title'], ['threat_next_review_date', 'next_review_date'], ['owner', 'assignedTo'], 'threats'),
      ...normaliseGeneric(remediations.data || [], 'Remediation', 'remediationId', ['title', 'remediation_title'], ['remediation_next_review_date', 'next_review_date'], ['owner', 'assignedTo'], 'remediations'),
      ...normaliseDocuments(documents.data || []),
      ...normalisePestleSettings(pestle.data ?? null),
      ...normaliseInterestedParties(parties.data || []),
      ...normaliseEvidenceSubmissions(pendingSubmissions.data || []),
    ];
    // Resolve personIds and registerIds to display names
    return raw.map((item) => ({
      ...item,
      owner: resolvePersonId(item.owner || '', personMap),
      register: registerMap[item.register] || item.register,
    }));
  }, [results, personMap, registerMap]);

  // Apply type + search filters, then split into sections sorted by date
  const { pastDue, thisWeek, future, noDate, total } = useMemo(() => {
    let items = allItems;
    if (filterType !== 'All') items = items.filter((i) => i.type === filterType);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.type.toLowerCase().includes(q) ||
          (i.owner || '').toLowerCase().includes(q) ||
          (i.register || '').toLowerCase().includes(q),
      );
    }

    const byDate = (a: TaskItem, b: TaskItem) => (a.dueDate!).localeCompare(b.dueDate!);

    const dated   = items.filter((i) => i.dueDate !== null);
    const noDate  = items.filter((i) => i.dueDate === null);

    const pastDue  = dated.filter((i) => getSection(i.dueDate!) === 'past-due').sort(byDate);
    const thisWeek = dated.filter((i) => getSection(i.dueDate!) === 'this-week').sort(byDate);
    const future   = dated.filter((i) => getSection(i.dueDate!) === 'future').sort(byDate);

    return { pastDue, thisWeek, future, noDate, total: dated.length };
  }, [allItems, filterType, search]);

  const formatDate = (d: string | null): React.ReactNode => {
    if (!d) return <span className="np-sub np-mb-0">—</span>;
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-calendar-check-line"
        title="Maintenance Tasks"
        subtitle="All items due for review across your GRC registers"
        colour="primary"
      />

      {/* Stats + Filters card */}
      <div className="np-card">
        <div className="np-card-head" style={{ flexWrap: 'wrap' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="ri-list-check-2 text-primary"></i>
            <h2 className="np-mb-0">Review Schedule</h2>
            <span className="np-chip np-tone-primary np-tabular">{isLoading ? '…' : total} scheduled</span>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
            {!isLoading && pastDue.length > 0 && (
              <span className="np-chip np-tone-danger np-tabular">
                <i className="ri-alarm-warning-line"></i>{pastDue.length} past due
              </span>
            )}
            {!isLoading && thisWeek.length > 0 && (
              <span className="np-chip np-tone-warning np-tabular">
                <i className="ri-calendar-event-line"></i>{thisWeek.length} this week
              </span>
            )}
            {!isLoading && future.length > 0 && (
              <span className="np-chip np-tone-success np-tabular">
                <i className="ri-calendar-line"></i>{future.length} future
              </span>
            )}
            {!isLoading && noDate.length > 0 && (
              <span className="np-chip np-tone-neutral np-tabular">
                <i className="ri-calendar-2-line"></i>{noDate.length} no date
              </span>
            )}
          </div>
        </div>
        <div className="np-card-body">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="np-search" style={{ maxWidth: 280 }}>
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Search by title, owner, register…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="np-icon-btn" onClick={() => setSearch('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            <select
              className="np-select"
              style={{ maxWidth: 170 }}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as ItemType | 'All')}
            >
              <option value="All">All Types</option>
              {ALL_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {(filterType !== 'All' || search) && (
              <button
                className="np-btn np-btn-ghost np-btn-sm"
                onClick={() => { setFilterType('All'); setSearch(''); }}
              >
                <i className="ri-filter-off-line"></i>Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="np-card">
          <div className="np-card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
            <p className="np-sub mt-2 mb-0">Loading all registers…</p>
          </div>
        </div>
      ) : total === 0 && noDate.length === 0 ? (
        <div className="np-card">
          <div className="np-card-body text-center py-5">
            <i className="ri-checkbox-circle-line ri-2x text-success mb-2 d-block"></i>
            <p className="np-sub mb-0">
              {filterType !== 'All' || search
                ? 'No items match your filters.'
                : 'No items with review dates found. Set review dates on your risks, controls, evidence and other items to track maintenance here.'}
            </p>
          </div>
        </div>
      ) : (
        <>
          <SectionTable section="past-due"  items={pastDue}  formatDate={formatDate} />
          <SectionTable section="this-week" items={thisWeek} formatDate={formatDate} />
          <SectionTable section="future"    items={future}   formatDate={formatDate} />
          {noDate.length > 0 && (
            <NoDateCallout items={noDate} />
          )}
        </>
      )}
    </div>
  );
};

export default MaintenanceTasks;
